import { useEffect, useRef, useState } from "react";

import { type PopupProps, Popup as SemanticPopup } from "semantic-ui-react";

import {
  FocusStackContext,
  createFocusTrap,
  useFocusStack,
} from "./utils/FocusStack";

type PopupComponentProps = PopupProps & { trapFocus?: boolean };

const Popup = ({ trapFocus, ...props }: PopupComponentProps) => {
  const focusStack = useFocusStack();

  const popupRef = useRef<HTMLDivElement>(null);

  const [returnFocusElement, setReturnFocusElement] =
    useState<HTMLElement | null>(null);
  const [internalOpen, setInternalOpen] = useState(props.open ?? false);

  useEffect(() => {
    if (!trapFocus) {
      return;
    }
    if (
      props.open !== undefined ? props.open === true : internalOpen === true
    ) {
      setReturnFocusElement(document.activeElement as HTMLElement);

      const focusTrap = createFocusTrap(popupRef.current);
      focusStack.insertFocusTrap(focusTrap);

      return () => {
        focusStack.removeFocusTrap(focusTrap);
      };
    }
  }, [trapFocus, props.open, focusStack, internalOpen]);

  useEffect(() => {
    if (!trapFocus) {
      return;
    }

    if (
      props.open !== undefined ? props.open === false : internalOpen === false
    ) {
      if (returnFocusElement && document.activeElement !== returnFocusElement) {
        returnFocusElement.focus();
      }
    }
  }, [trapFocus, props.open, internalOpen, returnFocusElement]);

  return (
    <FocusStackContext.Provider value={focusStack}>
      <SemanticPopup
        role="dialog"
        aria-modal="false"
        closeOnEscape
        ref={popupRef}
        {...props}
        onOpen={(e, data) => {
          setInternalOpen(true);
          props.onOpen?.(e, data);
        }}
        onClose={(e, data) => {
          setInternalOpen(false);
          props.onClose?.(e, data);
        }}
      />
    </FocusStackContext.Provider>
  );
};

Popup.Content = SemanticPopup.Content;

export { Popup };
